import { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { useIdleTimer } from "react-idle-timer";
import { useAuth, useOidc, useProfile } from "../../contexts";
import { useProfileQuery } from "../../queries";
import { config } from "../../config";

export function SessionTimeout() {
    const { logout, refresh } = useOidc();
    const { impersonator } = useProfile();
    const {
        unimpersonateMutation: { mutate: unimpersonateUser },
    } = useProfileQuery();
    const { decodedAccessToken, isAuthenticated, isPublicComputer } = useAuth();
    const inactivityThresholdSeconds = config.AUTO_LOGOUT_ON_IDLE_MINUTES * 60;
    const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());
    const [timeOfLastActivity, setTimeOfLastActivity] = useState<number>(new Date().getTime());
    const autoLogoutOnIdle = config.AUTO_LOGOUT_ON_IDLE_MINUTES > 0 && isPublicComputer;
    const autoRenewTokenThresholdSeconds = 5 * 60; // 5 minutes
    const showTimeoutModalThresholdSeconds = 2 * 60; // 2 minutes

    const handleOnAction = () => setTimeOfLastActivity(new Date().getTime());

    useIdleTimer({
        onAction: handleOnAction,
        events: ["keydown", "wheel", "mousedown", "touchstart"],
        element: document,
    });

    const secondsRemainingOnToken = useMemo((): number => {
        // decodedAccessToken.exp is the number of seconds since the epoch
        const result = Math.floor(decodedAccessToken.exp - currentTime / 1000);
        return result < 0 ? 0 : result;
    }, [currentTime, decodedAccessToken.exp]);

    const secondsSinceLastActivity = useMemo((): number => {
        const result = Math.floor((currentTime - timeOfLastActivity) / 1000);
        return result < 0 ? 0 : result;
    }, [currentTime, timeOfLastActivity]);

    const secondsRemainingBeforeAutoLogout = useMemo((): number => {
        const result = inactivityThresholdSeconds - secondsSinceLastActivity;
        return result < 0 ? 0 : result;
    }, [inactivityThresholdSeconds, secondsSinceLastActivity]);

    const showModal = useMemo(
        (): boolean =>
            isAuthenticated &&
            autoLogoutOnIdle &&
            secondsSinceLastActivity >= inactivityThresholdSeconds - showTimeoutModalThresholdSeconds,
        [
            isAuthenticated,
            autoLogoutOnIdle,
            secondsSinceLastActivity,
            inactivityThresholdSeconds,
            showTimeoutModalThresholdSeconds,
        ],
    );

    useEffect(() => {
        if (!isAuthenticated) return;
        const timer = setInterval(() => setCurrentTime(new Date().getTime()), 1000);
        return () => clearInterval(timer);
    }, [isAuthenticated]);

    useEffect(() => {
        if (secondsRemainingOnToken <= autoRenewTokenThresholdSeconds) {
            refresh();
        }
    }, [autoRenewTokenThresholdSeconds, refresh, secondsRemainingOnToken]);

    useEffect(() => {
        if (isAuthenticated && autoLogoutOnIdle && secondsRemainingBeforeAutoLogout <= 0) {
            if (impersonator) {
                unimpersonateUser(0);
            }
            logout();
        }
    }, [autoLogoutOnIdle, impersonator, isAuthenticated, logout, secondsRemainingBeforeAutoLogout, unimpersonateUser]);

    const timeRemaining = useMemo((): string => {
        const minutes = Math.floor(secondsRemainingBeforeAutoLogout / 60);
        const seconds = secondsRemainingBeforeAutoLogout - minutes * 60;
        const secondsDisplay = seconds < 10 ? `0${seconds}` : seconds;
        return minutes > 0 ? `${minutes}:${secondsDisplay}` : `${seconds} seconds`;
    }, [secondsRemainingBeforeAutoLogout]);

    const message = useMemo((): string => {
        return secondsRemainingBeforeAutoLogout > 0
            ? `Your session will expire in ${timeRemaining}.`
            : "Your session has expired. Please login again.";
    }, [secondsRemainingBeforeAutoLogout, timeRemaining]);

    return (
        <Modal backdrop="static" centered show={showModal} onHide={() => {}} style={{ zIndex: 99999 }}>
            <Modal.Header className="text-danger">Session Expiring Soon</Modal.Header>
            <Modal.Body>{message}</Modal.Body>
            <Modal.Footer>
                <Button>Stay Logged In</Button>
            </Modal.Footer>
        </Modal>
    );
}
